import React from 'react';
import { Table, Badge, Button } from 'react-bootstrap';
import { FaEdit, FaTrashAlt, FaCheckCircle, FaClone, FaEnvelope } from 'react-icons/fa';

const TransactionTable = ({ transactions, deleteTransaction, toggleComplete, handleSetEditing, cloneTransaction }) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(value || 0);
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString + 'T00:00:00').toLocaleDateString('es-CL', options);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const options = { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleString('es-CL', options);
  };

  const generateMailtoLink = (transaction) => {
    const subject = `Recordatorio: Transacción Vencida - ${transaction.description}`;
    const body = `Estimado/a,\n\nLe escribimos para recordarle que la siguiente transacción se encuentra vencida:\n\n- Descripción: ${transaction.description}\n- Fecha: ${formatDate(transaction.date)}\n- Monto: ${formatCurrency(transaction.amount)}\n\nAgradeceríamos que pudiera revisar el estado de esta transacción a la brevedad posible.\n\nSaludos cordiales.`;
    return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  if (!transactions || transactions.length === 0) {
    return <p className="text-center text-muted">No hay transacciones registradas.</p>;
  }

  return (
    <Table striped bordered hover responsive className="mt-3 table-fixed-layout table-compact">
      <thead>
        <tr>
          <th style={{ width: '5%' }}>Tipo</th>
          <th style={{ width: '8%' }}>Fecha</th>
          <th style={{ width: '12%', whiteSpace: 'normal', textAlign: 'center' }}>Empresa/<br />Conductor</th>
          <th style={{ width: '8%' }}>Monto</th>
          <th style={{ width: '8%' }}>Impacto Saldo</th>
          <th style={{ width: '10%' }}>Descripción</th>
          <th style={{ width: '8%', whiteSpace: 'normal', textAlign: 'center' }}>Boleta/<br />Factura</th>
          <th style={{ width: '8%', whiteSpace: 'normal', textAlign: 'center' }}>Solicitud/<br />Liquidación</th>
          <th style={{ width: '7%' }}>Observaciones</th>
          <th style={{ width: '7%' }}>Categoría</th>
          <th style={{ width: '7%' }}>Sub-Categoría</th>
          <th style={{ width: '5%' }}>Origen Impuesto</th>
          <th style={{ width: '8%' }}>Intereses DAP</th>
          <th style={{ width: '8%' }}>Comisión T/C</th>
          <th style={{ width: '8%' }}>Penalización Booking</th>
          <th style={{ width: '5%' }}>Estado</th>
          <th style={{ width: '8%' }}>Fecha Finalización</th>
          <th style={{ width: '8%' }}>Saldo Acumulado</th>
          <th style={{ width: '6%' }}>Acciones</th>
        </tr>
      </thead>
      <tbody>
        {transactions.map(transaction => {
          const isCompleted = transaction.status === 'completado';
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const transactionDate = new Date(transaction.date + 'T00:00:00');
          const isOverdue = !isCompleted && transaction.status !== 'incobrable' && transactionDate < today;

          const getRowClassName = () => {
            if (isCompleted) return 'table-success';
            if (transaction.status === 'incobrable') return 'table-secondary'; // Grey for uncollectible
            if (isOverdue) return 'table-danger';
            return '';
          };

          return (
            <tr key={transaction.id} className={getRowClassName()}>
              <td style={{ width: '5%' }}>
                <Badge bg={transaction.type === 'ingreso' ? 'success' : 'danger'}>
                  {transaction.type === 'ingreso' ? 'Ingreso' : 'Egreso'}
                </Badge>
              </td>
              <td style={{ width: '8%' }}>{formatDate(transaction.date)}</td>
              <td style={{ width: '12%' }}>{transaction.entity_name || '-'}</td>
              <td style={{ width: '8%' }}>{formatCurrency(transaction.amount)}</td>
              <td style={{ width: '8%' }}>{formatCurrency(transaction.balanceImpact)}</td>
              <td style={{ width: '10%' }}>{transaction.description}</td>
              <td style={{ width: '8%' }}>{transaction.invoice_number || '-'}</td>
              <td style={{ width: '8%' }}>{transaction.request_id || '-'}</td>
              <td style={{ width: '7%' }}>{transaction.notes || '-'}</td>
              <td style={{ width: '7%' }}><Badge bg="secondary">{transaction.category}</Badge></td>
              <td style={{ width: '7%' }}>{transaction.subcategory ? <Badge bg="info" text="dark">{transaction.subcategory}</Badge> : '-'}</td>
              <td style={{ width: '5%' }}>{transaction.tax_origin || '-'}</td>
              <td style={{ width: '8%' }}>{transaction.dap_interest ? formatCurrency(transaction.dap_interest) : '-'}</td>
              <td style={{ width: '8%' }}>{transaction.cc_commission ? formatCurrency(transaction.cc_commission) : '-'}</td>
              <td style={{ width: '8%' }}>{transaction.penalizaciones_booking ? formatCurrency(transaction.penalizaciones_booking) : '-'}</td>
              <td style={{ width: '5%' }}>
                {transaction.status === 'completado' ? (
                  <Badge bg="success">Completado</Badge>
                ) : transaction.status === 'Por Procesar' ? (
                  <Badge bg="primary">Por Procesar</Badge>
                ) : transaction.status === 'Esperando OC/HES' ? (
                  <Badge bg="info">Esperando OC/HES</Badge>
                ) : transaction.status === 'incobrable' ? (
                  <Badge bg="dark">Incobrable</Badge>
                ) : (
                  <Badge bg={isOverdue ? 'danger' : 'warning'} text={isOverdue ? 'white' : 'dark'}>
                    {isOverdue ? 'Vencida' : 'Pendiente'}
                  </Badge>
                )}
              </td>
              <td style={{ width: '8%' }}>{formatDateTime(transaction.completion_date)}</td>
              <td style={{ width: '8%' }}>{typeof transaction.runningBalance === 'number' ? formatCurrency(transaction.runningBalance) : '-'}</td>
              <td style={{ width: '6%' }}>
                <div className="d-flex gap-2">
                  {isOverdue && (
                    <Button as="a" href={generateMailtoLink(transaction)} variant="outline-warning" size="sm" title="Enviar Mail">
                      <FaEnvelope />
                    </Button>
                  )}
                  {!isCompleted && transaction.status !== 'incobrable' && (
                    <Button variant="outline-success" size="sm" title="Marcar como completada" onClick={() => toggleComplete(transaction.id)}>
                      <FaCheckCircle />
                    </Button>
                  )}
                  <Button variant="outline-secondary" size="sm" title="Clonar" onClick={() => cloneTransaction(transaction.id)}>
                    <FaClone />
                  </Button>
                  <Button variant="outline-primary" size="sm" title="Editar" onClick={() => handleSetEditing(transaction)}>
                    <FaEdit />
                  </Button>
                  <Button variant="outline-danger" size="sm" title="Eliminar" onClick={() => deleteTransaction(transaction.id)}>
                    <FaTrashAlt />
                  </Button>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};

export default TransactionTable;
